import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addGameCodeToTitle, removeGameCode, getGameByID } from "@/lib/gameCategories";
import { useEffect } from "react";

interface FormulationStepProps {
  formData: any;
  updateFormData: (data: any) => void;
}

const categories = ["cs2", "dota2", "lol", "valorant", "fortnite", "overwatch", "apex", "rocketleague", "smash", "fgc", "mobile", "other"];

const FormulationStep = ({ formData, updateFormData }: FormulationStepProps) => {
  // Auto-add game code when category changes
  useEffect(() => {
    if (formData.category && formData.title) {
      const cleanTitle = removeGameCode(formData.title);
      const newTitle = addGameCodeToTitle(cleanTitle, formData.category);
      if (newTitle !== formData.title) {
        updateFormData({ title: newTitle });
      }
    }
  }, [formData.category]);

  const handleTitleChange = (value: string) => {
    // User types the clean title, we add the game code automatically
    if (formData.category) {
      // If category is selected, add the game code to whatever they type
      const titleWithCode = addGameCodeToTitle(value, formData.category);
      updateFormData({ title: titleWithCode });
    } else {
      // If no category, just update the title
      updateFormData({ title: value });
    }
  };
  
  // Get the display value (without game code) for the input field
  const displayTitle = formData.title ? removeGameCode(formData.title) : "";

  // Show display title (without code) in placeholder if category is selected
  const getPlaceholder = () => {
    if (formData.category) {
      const game = getGameByID(formData.category);
      return `${game?.code}: Will Team Liquid beat Natus Vincere?`;
    }
    return "e.g., Will Team Liquid beat Natus Vincere in The International 2025 Finals?";
  };

  return (
    <div className="space-y-6">
      {/* Category - Moved to top for better UX */}
      <div className="space-y-2">
        <Label htmlFor="category">Game *</Label>
        <Select
          value={formData.category}
          onValueChange={(value) => updateFormData({ category: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a game first" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cs2">Counter-Strike 2</SelectItem>
            <SelectItem value="dota2">Dota 2</SelectItem>
            <SelectItem value="lol">League of Legends</SelectItem>
            <SelectItem value="valorant">Valorant</SelectItem>
            <SelectItem value="fortnite">Fortnite</SelectItem>
            <SelectItem value="overwatch">Overwatch</SelectItem>
            <SelectItem value="apex">Apex Legends</SelectItem>
            <SelectItem value="rocketleague">Rocket League</SelectItem>
            <SelectItem value="smash">Super Smash Bros</SelectItem>
            <SelectItem value="fgc">Fighting Games</SelectItem>
            <SelectItem value="mobile">Mobile Esports</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Select the game - a 2-letter code will be added to your market title automatically
        </p>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">Match Question *</Label>
        <Input
          id="title"
          placeholder={getPlaceholder()}
          value={displayTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="text-base"
          disabled={!formData.category}
        />
        <p className="text-xs text-muted-foreground">
          {formData.category 
            ? `Your question will be saved as: "${formData.title || `${getGameByID(formData.category)?.code}: ...`}"`
            : "Select a game first, then write your question"
          }
        </p>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Resolution Statement *</Label>
        <Textarea
          id="description"
          placeholder="Describe exactly what conditions will cause this match to resolve YES vs NO. Include match format (BO3, BO5), tournament round, and specific win conditions. Be as specific as possible to avoid ambiguity."
          value={formData.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
          rows={5}
          className="text-base"
        />
        <div className="flex items-center justify-between text-xs">
          <p className="text-muted-foreground">
            This is the most important part - be extremely clear about match outcome and resolution criteria
          </p>
          <span className={`font-mono ${
            formData.description && formData.description.length > 50 
              ? "text-green-500" 
              : "text-muted-foreground"
          }`}>
            {formData.description?.length || 0} / 50 min
          </span>
        </div>
      </div>

      {/* Match Image */}
      <div className="space-y-2">
        <Label htmlFor="marketIcon">Match Image URL (Optional)</Label>
        <Input
          id="marketIcon"
          placeholder="e.g., https://example.com/team-logos.png"
          value={formData.marketIcon}
          onChange={(e) => updateFormData({ marketIcon: e.target.value })}
          className="text-base"
        />
        <p className="text-xs text-muted-foreground">
          Provide a URL to an image for the match (team logos, tournament banner, etc.). Leave empty for default game icon.
        </p>
      </div>

      {/* Truth Source */}
      <div className="space-y-2">
        <Label htmlFor="truthSource">Source of Truth *</Label>
        <Input
          id="truthSource"
          placeholder="e.g., HLTV.org, Liquipedia, Official Tournament Stream"
          value={formData.truthSource}
          onChange={(e) => updateFormData({ truthSource: e.target.value })}
          className="text-base"
        />
        <p className="text-xs text-muted-foreground">
          Where will the match result come from? Be specific (official tournament page, stats site, etc.).
        </p>
      </div>

      {/* Invalidity Conditions */}
      <div className="space-y-2">
        <Label htmlFor="invalidityConditions">Invalidity Conditions (Optional)</Label>
        <Textarea
          id="invalidityConditions"
          placeholder="Under what circumstances should this match be invalidated? (e.g., match cancelled, DQ, technical issues)"
          value={formData.invalidityConditions}
          onChange={(e) => updateFormData({ invalidityConditions: e.target.value })}
          rows={3}
          className="text-base"
        />
        <p className="text-xs text-muted-foreground">
          Define edge cases where the match should resolve as invalid (e.g., forfeit, match cancelled, cheating detected)
        </p>
      </div>
    </div>
  );
};

export default FormulationStep;
